import { Award, Cake, Calendar, Crown, Edit, Gift, Minus, Star } from "lucide-react";
import { DateTime } from "luxon";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getCustomerPointsToNextTier } from "@/domains/customer/customers/utils/customer-utils";
import type { CustomerDetailFragment, PointRewardFragment } from "@/gql/graphql";
import { useConfig } from "@/hooks/configuration";
import { timezoneUtils } from "@/lib/timezone";

interface CustomerLoyaltyCardProps {
  customer: CustomerDetailFragment;
  loading?: boolean;
}

export function CustomerLoyaltyCard({ customer, loading = false }: CustomerLoyaltyCardProps) {
  const { value: loyaltyEnabled = false } = useConfig("loyalty.enabled");
  const tierName = customer.currentTier?.name || "No Tier";
  const pointsToNextTier = getCustomerPointsToNextTier(customer);

  // If loyalty is disabled, don't render the card
  if (!loyaltyEnabled) {
    return null;
  }

  const allRewards: PointRewardFragment[] = [];

  // Filter appointment-related rewards
  const appointmentRewards = allRewards.filter((reward) => reward.type === "APPOINTMENT_COMPLETED");

  // Filter non-appointment rewards
  const nonAppointmentRewards = allRewards.filter(
    (reward) => reward.type !== "APPOINTMENT_COMPLETED"
  );

  const getRewardTypeInfo = (type: string) => {
    switch (type) {
      case "APPOINTMENT_COMPLETED":
        return {
          icon: Calendar,
          label: "Appointment",
          color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
        };
      case "BIRTHDAY_BONUS":
        return {
          icon: Cake,
          label: "Birthday Bonus",
          color: "bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200",
        };
      case "REFERRAL_BONUS":
        return {
          icon: Gift,
          label: "Referral Bonus",
          color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
        };
      case "SIGNUP_BONUS":
        return {
          icon: Star,
          label: "Welcome Bonus",
          color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
        };
      case "MANUAL_ADJUSTMENT":
        return {
          icon: Edit,
          label: "Manual Adjustment",
          color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
        };
      case "REDEMPTION":
        return {
          icon: Minus,
          label: "Points Redemption",
          color: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
        };
      case "TIER_BONUS":
        return {
          icon: Crown,
          label: "Tier Bonus",
          color: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
        };
      default:
        return {
          icon: Award,
          label: type.replace("_", " "),
          color: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200",
        };
    }
  };

  const formatPoints = (points: number) => {
    if (points > 0) {
      return <span className="text-green-600 dark:text-green-400 font-medium">+{points}</span>;
    }
    if (points < 0) {
      return <span className="text-red-600 dark:text-red-400 font-medium">{points}</span>;
    }
    return <span className="text-muted-foreground font-medium">0</span>;
  };

  const formatDate = (dateString: string) => {
    return timezoneUtils.fromISO(dateString).toLocaleString(DateTime.DATE_MED);
  };

  const RewardsTable = ({ rewards }: { rewards: PointRewardFragment[] }) => (
    <div className="space-y-3">
      {rewards.length > 0 ? (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Points</TableHead>
                  <TableHead className="text-right">Balance After</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rewards.slice(0, 10).map((reward) => {
                  const typeInfo = getRewardTypeInfo(reward.type);
                  const Icon = typeInfo.icon;

                  return (
                    <TableRow key={reward.id}>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(reward.createdAt)}
                      </TableCell>
                      <TableCell>
                        <Badge className={typeInfo.color}>
                          <Icon className="w-3 h-3 mr-1" />
                          {typeInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">{reward.description}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatPoints(reward.points)}
                      </TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">
                        {reward.totalPointsAfter}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {rewards.slice(0, 10).map((reward) => {
              const typeInfo = getRewardTypeInfo(reward.type);
              const Icon = typeInfo.icon;

              return (
                <div key={reward.id} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <Badge className={typeInfo.color}>
                      <Icon className="w-3 h-3 mr-1" />
                      {typeInfo.label}
                    </Badge>
                    <div className="text-right">
                      <div className="font-medium">{formatPoints(reward.points)}</div>
                      <div className="text-xs text-muted-foreground">
                        Balance: {reward.totalPointsAfter}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">{reward.description}</p>
                  <div className="text-xs text-muted-foreground">
                    {formatDate(reward.createdAt)}
                  </div>
                </div>
              );
            })}
          </div>

          {rewards.length > 10 && (
            <div className="text-center text-sm text-muted-foreground">
              Showing 10 of {rewards.length} rewards
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Award className="w-8 h-8 text-muted-foreground" />
          </div>
          <p className="text-sm text-muted-foreground">No rewards found</p>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="w-5 h-5" />
            Loyalty & Rewards
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="animate-pulse">
              <div className="h-20 bg-muted rounded-lg mb-4" />
              <div className="h-32 bg-muted rounded-lg" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Star className="w-5 h-5" />
          Loyalty & Rewards
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Loyalty Points Summary */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/20 dark:to-purple-950/20 p-4 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">
                {customer.totalPoints}
              </div>
              <div className="text-sm text-indigo-900 dark:text-indigo-100">Total Points</div>
            </div>
            <div
              className="px-3 py-1 rounded-full text-sm font-medium capitalize text-white"
              style={{ backgroundColor: customer.currentTier?.color || "#64748b" }}
            >
              {tierName} Tier
            </div>
          </div>

          {pointsToNextTier > 0 && (
            <div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
                <div
                  className="bg-indigo-600 dark:bg-indigo-500 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${((customer.totalPoints % 500) / 500) * 100}%`,
                  }}
                />
              </div>
              <div className="text-xs text-indigo-700 dark:text-indigo-300">
                {pointsToNextTier} points to next tier
              </div>
            </div>
          )}
        </div>

        {/* Rewards Tabs */}
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="all" className="text-xs">
              All ({allRewards.length})
            </TabsTrigger>
            <TabsTrigger value="appointments" className="text-xs">
              Appointments ({appointmentRewards.length})
            </TabsTrigger>
            <TabsTrigger value="other" className="text-xs">
              Other ({nonAppointmentRewards.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-4">
            <RewardsTable rewards={allRewards} />
          </TabsContent>

          <TabsContent value="appointments" className="mt-4">
            <RewardsTable rewards={appointmentRewards} />
          </TabsContent>

          <TabsContent value="other" className="mt-4">
            <RewardsTable rewards={nonAppointmentRewards} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
